/**
 * PopulationAnalysisPanel — V2.1
 *
 * Panel izquierdo para el modo análisis de población por segmento.
 * Se muestra cuando operationalMode === 'analyze' && activeIndicator === 'population'.
 *
 * V2.1: Agrega selector de método (census_network_length / building_refined).
 * El método default sigue siendo census_network_length.
 */

import { Users, RefreshCw, AlertCircle, CheckCircle2, ChevronDown, Database, Building2 } from 'lucide-react';
import { usePopulationSegment, type PopulationVariable, type PopulationMethod } from '../../../spatial/contexts/PopulationSegmentContext';
import { COLOR_RAMPS, DEFAULT_SYNTAX_RAMP } from '../../../spatial/config/syntax-color-ramps';
import './population-analysis-panel.css';

// ============================================================================
// CONSTANTS
// ============================================================================

const VARIABLE_LABELS: Record<PopulationVariable, string> = {
  population_per_km:      'Población / km',
  population:             'Población asignada',
  households_per_km:      'Hogares / km',
  households:             'Hogares asignados',
  nbi_households_per_km:  'Hogares NBI / km',
  nbi_households:         'Hogares NBI asignados',
};

const VARIABLE_DESCRIPTIONS: Record<PopulationVariable, string> = {
  population_per_km:     'Densidad de población por kilómetro de red. Permite comparar intensidad urbana entre segmentos de distinta longitud.',
  population:            'Población total asignada al segmento. Favorece segmentos largos.',
  households_per_km:     'Hogares estimados por kilómetro de red.',
  households:            'Total de hogares asignados al segmento.',
  nbi_households_per_km: 'Hogares con Necesidades Básicas Insatisfechas por kilómetro de red.',
  nbi_households:        'Total de hogares NBI asignados al segmento.',
};

const VARIABLE_UNIT: Record<PopulationVariable, string> = {
  population_per_km:     'hab/km',
  population:            'hab.',
  households_per_km:     'hog./km',
  households:            'hog.',
  nbi_households_per_km: 'NBI/km',
  nbi_households:        'hog. NBI',
};

const METHOD_LABELS: Record<PopulationMethod, string> = {
  census_network_length: 'Base censal-red',
  building_refined:      'Refinado edilicio',
};

const METHOD_NOTES: Record<PopulationMethod, string> = {
  census_network_length: 'Distribuye población censal sobre la red según longitud de segmento dentro de cada radio.',
  building_refined:      'Distribuye población censal hacia edificios elegibles y luego a sus segmentos asociados.',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function PopulationAnalysisPanel(): JSX.Element {
  const {
    selectedMethod,
    setSelectedMethod,
    runStatus,
    runMetadata,
    isLoading,
    isCalculating,
    errorMessage,
    ranges,
    selectedVariable,
    setSelectedVariable,
    runCalculation,
    runBuildingRefinedCalculation,
  } = usePopulationSegment();

  const ramp = COLOR_RAMPS[DEFAULT_SYNTAX_RAMP];
  const gradientStops = ramp.stops.map((s) => `${s.color} ${s.stop * 100}%`).join(', ');
  const currentRange = ranges[selectedVariable];
  const unit = VARIABLE_UNIT[selectedVariable];

  const formatValue = (v: number): string => {
    const num =
      v >= 1000   ? `${(v / 1000).toFixed(1)}k` :
      v >= 100    ? v.toFixed(0) :
      v >= 1      ? v.toFixed(1) :
      v > 0       ? v.toFixed(2) : '0';
    return `${num} ${unit}`;
  };

  const handleRun = async (): Promise<void> => {
    try {
      if (selectedMethod === 'building_refined') {
        await runBuildingRefinedCalculation();
      } else {
        await runCalculation();
      }
    } catch (e) {
      console.error('[PopulationAnalysisPanel] Error launching run:', e);
    }
  };

  const coveragePct =
    runMetadata?.assignmentPct != null
      ? `${runMetadata.assignmentPct.toFixed(1)}%`
      : null;

  const isRunning = isCalculating || runStatus === 'running' || runStatus === 'pending';

  const isRefined = selectedMethod === 'building_refined';

  return (
    <div className="pop-ap">

      {/* ── HEADER ── */}
      <div className="pop-ap__header">
        <div className="pop-ap__title-row">
          <Users size={14} className="pop-ap__icon" />
          <span className="pop-ap__title">Población por segmento</span>
        </div>

        <div className="pop-ap__badges">
          {runStatus === 'completed' && !isLoading && (
            <span className="pop-ap__badge pop-ap__badge--ready">
              <CheckCircle2 size={10} />
              {isRefined ? 'Refinado edilicio' : 'Base censal'}
            </span>
          )}
          {isRunning && (
            <span className="pop-ap__badge pop-ap__badge--calculating">
              <RefreshCw size={10} className="pop-ap__spinner" />
              Calculando
            </span>
          )}
          {runStatus === 'no_runs' && !isRunning && (
            <span className="pop-ap__badge pop-ap__badge--no-data">
              Sin datos
            </span>
          )}
          {runStatus === 'failed' && !isRunning && (
            <span className="pop-ap__badge pop-ap__badge--error">
              <AlertCircle size={10} />
              Error
            </span>
          )}
        </div>
      </div>

      {/* ── SELECTOR DE MÉTODO ── */}
      <div className="pop-ap__method-selector">
        <button
          className={`pop-ap__method-btn${!isRefined ? ' pop-ap__method-btn--active' : ''}`}
          onClick={() => setSelectedMethod('census_network_length')}
          disabled={isRunning}
          title="Base censal-red"
        >
          Base censal-red
        </button>
        <button
          className={`pop-ap__method-btn${isRefined ? ' pop-ap__method-btn--active' : ''}`}
          onClick={() => setSelectedMethod('building_refined')}
          disabled={isRunning}
          title="Refinado edilicio"
        >
          <Building2 size={11} />
          Refinado edilicio
        </button>
      </div>

      {/* ── CALCULANDO ── */}
      {isRunning && (
        <div className="pop-ap__status pop-ap__status--calculating">
          <RefreshCw size={12} className="pop-ap__spinner" />
          <span>Calculando {METHOD_LABELS[selectedMethod]}… aguarda</span>
        </div>
      )}

      {/* ── ERROR ── */}
      {runStatus === 'failed' && !isRunning && (
        <div className="pop-ap__status pop-ap__status--error">
          <AlertCircle size={12} />
          <span>{errorMessage ?? 'El cálculo falló. Intenta de nuevo.'}</span>
          <button
            className="pop-ap__run-btn"
            onClick={handleRun}
            disabled={isRunning || isLoading}
          >
            <RefreshCw size={13} />
            Reintentar
          </button>
        </div>
      )}

      {/* ── SIN DATOS + BOTÓN CALCULAR ── */}
      {runStatus === 'no_runs' && !isRunning && (
        <div className="pop-ap__empty">
          <Database size={24} className="pop-ap__empty-icon" />
          <p className="pop-ap__empty-title">
            {isRefined ? 'Refinado edilicio no calculado' : 'Sin datos de población'}
          </p>
          <p className="pop-ap__empty-body">
            {isRefined
              ? 'Calculá la distribución poblacional usando edificios elegibles como ponderadores espaciales.'
              : 'Calculá la distribución de población por segmento de red usando la base censal 2022.'}
          </p>
          <button
            className="pop-ap__run-btn"
            onClick={handleRun}
            disabled={isRunning || isLoading}
          >
            <RefreshCw size={14} />
            {isRefined ? 'Calcular refinado edilicio' : 'Calcular población'}
          </button>
        </div>
      )}

      {/* ── DATOS DISPONIBLES ── */}
      {runStatus === 'completed' && !isLoading && (
        <>
          {/* Selector de variable */}
          <div className="pop-ap__section">
            <label className="pop-ap__label">Variable visualizada</label>
            <div className="pop-ap__select-wrapper">
              <select
                className="pop-ap__select"
                value={selectedVariable}
                onChange={(e) => setSelectedVariable(e.target.value as PopulationVariable)}
              >
                {(Object.entries(VARIABLE_LABELS) as [PopulationVariable, string][]).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
              <ChevronDown size={12} className="pop-ap__select-icon" />
            </div>
            <p className="pop-ap__description">{VARIABLE_DESCRIPTIONS[selectedVariable]}</p>
          </div>

          {/* Gradiente de color */}
          {currentRange && (
            <div className="pop-ap__gradient-section">
              <div
                className="pop-ap__gradient"
                style={{ background: `linear-gradient(to right, ${gradientStops})` }}
              />
              <div className="pop-ap__grad-labels">
                <span className="pop-ap__grad-value">{formatValue(currentRange.min)}</span>
                <span className="pop-ap__grad-value">{formatValue(currentRange.p95)}</span>
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="pop-ap__meta">
            <div className="pop-ap__meta-row">
              <span className="pop-ap__meta-label">Método</span>
              <span className="pop-ap__meta-value">{METHOD_LABELS[selectedMethod]}</span>
            </div>

            {coveragePct && (
              <div className="pop-ap__meta-row">
                <span className="pop-ap__meta-label">Cobertura</span>
                <span className="pop-ap__meta-value">{coveragePct}</span>
              </div>
            )}

            {runMetadata?.totalPopulationInput != null && (
              <div className="pop-ap__meta-row">
                <span className="pop-ap__meta-label">Población</span>
                <span className="pop-ap__meta-value">
                  {Math.round(runMetadata.totalPopulationInput).toLocaleString('es-AR')} hab.
                </span>
              </div>
            )}

            {runMetadata?.totalPopulationUnassigned != null && runMetadata.totalPopulationUnassigned > 0 && (
              <div className="pop-ap__meta-row">
                <span className="pop-ap__meta-label">No asignada</span>
                <span className="pop-ap__meta-value">
                  {Math.round(runMetadata.totalPopulationUnassigned).toLocaleString('es-AR')} hab.
                </span>
              </div>
            )}

            {isRefined && runMetadata?.edgeCount != null && (
              <div className="pop-ap__meta-row">
                <span className="pop-ap__meta-label">Segmentos</span>
                <span className="pop-ap__meta-value">
                  {runMetadata.edgeCount.toLocaleString('es-AR')}
                </span>
              </div>
            )}

            {runMetadata?.completedAt && (
              <div className="pop-ap__meta-row">
                <span className="pop-ap__meta-label">Calculado</span>
                <span className="pop-ap__meta-value">
                  {new Date(runMetadata.completedAt).toLocaleDateString('es-ES', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </span>
              </div>
            )}
          </div>

          {/* Nota metodológica */}
          <p className="pop-ap__method-note">
            {METHOD_NOTES[selectedMethod]}
          </p>

          {/* Botón Recalcular (solo método base; refinado no se recalcula automáticamente) */}
          {!isRefined && (
            <button
              className="pop-ap__run-btn"
              onClick={handleRun}
              disabled={isRunning || isLoading}
            >
              <RefreshCw size={13} />
              Recalcular
            </button>
          )}
        </>
      )}

    </div>
  );
}
